import { IPublicClientApplication } from '@azure/msal-browser';
import { getToken, getAccount } from '../auth/authService';
import { discoverEnvironments } from './discoveryService';
import { getBeaconEnvUrl, setBeaconEnvUrl } from './tenantConfigService';

// ── Cross-environment Beacon (Dagrun CRM) resolution ──────────────────
//
// Dagrun PM, Dagrun CRM (Beacon), and Dagrun for Outlook run in SEPARATE
// Dataverse environments by design. Beacon's tables (dagrun_deal, …) therefore
// do NOT live in the PMIS environment — the old same-environment probe against
// getDataverseApiBase() always 404'd regardless of whether Beacon was installed
// in the tenant. This resolves Beacon properly:
//   1. Enumerate the tenant's environments via the Global Discovery Service
//      (reuses discoverEnvironments — same pattern as the license/redeem work).
//   2. For each, SILENTLY acquire a token for that environment's resource and
//      probe EntityDefinitions(LogicalName='dagrun_deal') — 200 = Beacon lives
//      here. Silent-only so a background probe never triggers a consent popup
//      per environment; environments the user hasn't consented to are skipped.
//   3. Cache the resolved base URL in the tenant config (dagrun_pm_beaconenvurl)
//      so every other user/session skips re-discovery.
// All Beacon reads/writes then target the RESOLVED environment's Web API with a
// token for that environment.

let cachedBase: string | null | undefined = undefined; // undefined = unresolved this session
let inflight: Promise<string | null> | null = null;

function originOf(url: string): string {
  try { return new URL(url).origin; }
  catch { return url.replace(/\/api\/.*/i, '').replace(/\/+$/, ''); }
}

/** Silent token for an arbitrary environment resource; null when not silently
 *  obtainable (unconsented / no access) — the probe skips those environments. */
async function silentToken(pca: IPublicClientApplication, base: string): Promise<string | null> {
  try {
    const account = await getAccount(pca);
    const res = await pca.acquireTokenSilent({ scopes: [`${base}/user_impersonation`], account: account ?? undefined });
    return res.accessToken;
  } catch {
    return null;
  }
}

async function envHasDeal(base: string, token: string): Promise<boolean> {
  try {
    const res = await fetch(`${base}/api/data/v9.2/EntityDefinitions(LogicalName='dagrun_deal')?$select=LogicalName`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    return res.status === 200;
  } catch {
    return false;
  }
}

/**
 * Resolve the base URL of the Dataverse environment where Beacon is installed,
 * or null when Beacon is not present in the tenant. Cached per-session and
 * persisted to the tenant config. Never throws.
 */
export async function resolveBeaconBase(pca: IPublicClientApplication): Promise<string | null> {
  if (cachedBase !== undefined) return cachedBase;
  if (inflight) return inflight;
  inflight = (async () => {
    // 1) Persisted tenant-config cache — trust it only if it still resolves.
    try {
      const saved = await getBeaconEnvUrl(pca);
      if (saved) {
        const base = originOf(saved);
        const tok = await silentToken(pca, base);
        if (tok && await envHasDeal(base, tok)) { cachedBase = base; return base; }
      }
    } catch { /* fall through to discovery */ }

    // 2) Global Discovery + per-environment dagrun_deal probe.
    try {
      const envs = await discoverEnvironments(pca);
      for (const e of envs) {
        const base = originOf(e.apiUrl);
        const tok = await silentToken(pca, base);
        if (!tok) continue; // unconsented / inaccessible — skip silently
        if (await envHasDeal(base, tok)) {
          cachedBase = base;
          setBeaconEnvUrl(pca, base).catch(() => { /* best effort; session cache still holds */ });
          return base;
        }
      }
      cachedBase = null; // discovery succeeded, Beacon genuinely absent
      return null;
    } catch {
      // Discovery itself failed — leave UNCACHED so a transient failure retries.
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** True when Beacon (Dagrun CRM) is installed in ANY environment in the tenant. */
export async function isBeaconInstalled(pca: IPublicClientApplication): Promise<boolean> {
  return (await resolveBeaconBase(pca)) !== null;
}

/** Force a re-resolve (admin debug). Clears only the session cache. */
export function _resetBeaconDetectionCache(): void { cachedBase = undefined; inflight = null; }

// ── Beacon-environment Web API (targets the resolved environment) ──────

async function beaconFetch(pca: IPublicClientApplication, method: string, path: string, body?: unknown): Promise<Response> {
  const base = await resolveBeaconBase(pca);
  if (!base) throw new Error('Beacon (Dagrun CRM) is not installed in this tenant.');
  // Active use of a Beacon surface — getToken may prompt once for consent to
  // the Beacon environment; that is intentional here (unlike the background probe).
  const token = await getToken(pca, [`${base}/user_impersonation`]);
  return fetch(`${base}/api/data/v9.2/${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      'OData-MaxVersion': '4.0',
      'OData-Version': '4.0',
      Prefer: method === 'POST' ? 'return=representation' : 'return=minimal',
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export interface BeaconDeal {
  dagrun_dealid: string;
  dagrun_name: string;
  dagrun_value?: number | null;
  dagrun_iswon?: boolean | null;
  _dagrun_accountid_value?: string | null;
  '_dagrun_accountid_value@OData.Community.Display.V1.FormattedValue'?: string;
}

/** Search Beacon deals by name in the resolved Beacon environment. */
export async function searchBeaconDeals(pca: IPublicClientApplication, query: string): Promise<BeaconDeal[]> {
  const trimmed = query.trim().replace(/'/g, "''");
  const filter = trimmed ? `&$filter=contains(dagrun_name,'${trimmed}')` : '';
  const res = await beaconFetch(pca, 'GET',
    `dagrun_deals?$select=dagrun_dealid,dagrun_name,dagrun_value,dagrun_iswon,_dagrun_accountid_value&$orderby=dagrun_name asc&$top=25${filter}`);
  if (!res.ok) return [];
  const data = await res.json() as { value?: BeaconDeal[] };
  return data.value ?? [];
}

/** Create a Beacon opportunity (deal) from an RFP. Returns the new dagrun_dealid. */
export async function createBeaconDeal(pca: IPublicClientApplication, input: { name: string; value?: number | null }): Promise<string> {
  const body: Record<string, unknown> = { dagrun_name: input.name };
  if (input.value != null) body.dagrun_value = input.value;
  const res = await beaconFetch(pca, 'POST', 'dagrun_deals', body);
  if (!res.ok) throw new Error(`Create Beacon deal failed (${res.status}): ${(await res.text()).slice(0, 300)}`);
  const created = await res.json() as { dagrun_dealid: string };
  return created.dagrun_dealid;
}

/**
 * Mark a linked Beacon deal as Won/Delivery when its RFP is won.
 *
 * METADATA-CONFIRMED (read live from the Beacon environment's dagrun_deal
 * schema, not guessed): the deal has NO won status-integer — statuscode is only
 * Active(1)/Inactive(2), and the dagrun_stage lookup is unpopulated. The
 * authoritative Won flag is the boolean dagrun_iswon; we also stamp
 * dagrun_closeddate and dagrun_probability=100 to match a closed-won deal.
 */
export async function markBeaconDealWon(pca: IPublicClientApplication, dealId: string): Promise<void> {
  const res = await beaconFetch(pca, 'PATCH', `dagrun_deals(${dealId})`, {
    dagrun_iswon: true,
    dagrun_closeddate: new Date().toISOString(),
    dagrun_probability: 100,
  });
  if (!res.ok) throw new Error(`Update Beacon deal stage failed (${res.status}): ${(await res.text()).slice(0, 300)}`);
}
